// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// graph.ts — rooms, walking cost, and the cost fields everything else is derived from.
//
// THE SEVERANCE DETECTOR IS FREE. `field[tile] === INF` in the field grown from the larder IS the
// fact that a wing has been cut off — the HUD's `chain N` badge, the Reach overlay, the job filter
// that stops a hauler pointlessly trying, the alert rail and the run summary's severance log are all
// the same array read five ways. That is why routing is fields rather than per-agent A*: few
// sources, many readers, and the interesting quantity is the whole field, not one path.
//
// THE ORDER OF DERIVATION IS ACYCLIC AND IT MATTERS: snow depth -> door jam -> rooms -> cost ->
// fields. An earlier sketch had a door accumulating snow only while open and jamming at depth 2,
// which is circular — it could never jam, so the room could never leak, so interaction C could never
// happen. A door tile always accumulates: it is a threshold, and people keep opening it.

import { COLS, ROWS, TILES, Terrain, colOf, rowOf } from './map';
import { Build, MAX_DEPTH } from './modes';

/** Unreachable. Large enough to dwarf any real path, small enough to stay a comfortable int32. */
export const INF = 0x3fffffff;

/** The room id given to every tile connected to the border. The outdoors is never heated. */
export const OUTDOOR = 0;

export interface GraphInput {
  terrain: Uint8Array;
  /** Build.* + 1, or 0 for nothing. */
  build: Uint8Array;
  depth: Uint8Array;
}

/** A door at this depth or above cannot be pulled shut, so it stops enclosing anything. */
export const JAM_DEPTH = 2;

export function isDoor(build: Uint8Array, t: number): boolean {
  return build[t] === Build.Door + 1;
}

export function isJammed(build: Uint8Array, depth: Uint8Array, t: number): boolean {
  return isDoor(build, t) && depth[t] >= JAM_DEPTH;
}

/** Rock and walls stop everything. A door stops enclosure but not people — unless it is jammed. */
export function solidForRooms(g: GraphInput, t: number): boolean {
  if (g.terrain[t] === Terrain.Rock) return true;
  if (g.build[t] === Build.Wall + 1) return true;
  if (isDoor(g.build, t)) return !isJammed(g.build, g.depth, t);
  return false;
}

/** Can a colonist stand here? Depth 3 is impassable; a door never is. */
export function passable(g: GraphInput, t: number): boolean {
  if (g.terrain[t] === Terrain.Rock) return false;
  if (g.build[t] === Build.Wall + 1) return false;
  if (isDoor(g.build, t)) return true;
  return g.depth[t] < MAX_DEPTH;
}

/** Ticks to step ONTO this tile. A door costs one, whatever is piled against it. */
export function stepCost(g: GraphInput, t: number): number {
  if (!passable(g, t)) return INF;
  if (isDoor(g.build, t)) return 1;
  return 1 + g.depth[t];
}

/**
 * Flood-fill rooms over everything that is not solid-for-rooms. Any component touching the border is
 * OUTDOOR (0); the rest are numbered from 1. "Indoors" is therefore a topological fact rather than a
 * radius, which is the whole differentiation from the fleet's other cold-survival game, and M6
 * re-derives this same fill independently to prove the heat obeys it.
 */
export function computeRooms(g: GraphInput, out: Int32Array): number {
  out.fill(-1);
  const stack = new Int32Array(TILES);
  let next = 1;

  for (let seed = 0; seed < TILES; seed++) {
    if (out[seed] !== -1) continue;
    if (solidForRooms(g, seed)) {
      out[seed] = OUTDOOR;
      continue;
    }
    // Collect the component first, then decide whether it touched the border.
    let sp = 0;
    let count = 0;
    const members: number[] = [];
    stack[sp++] = seed;
    out[seed] = -2; // in progress
    let touchesBorder = false;
    while (sp > 0) {
      const t = stack[--sp];
      members.push(t);
      count++;
      const c = colOf(t);
      const r = rowOf(t);
      if (c === 0 || r === 0 || c === COLS - 1 || r === ROWS - 1) touchesBorder = true;
      if (r > 0) push(t - COLS);
      if (r < ROWS - 1) push(t + COLS);
      if (c > 0) push(t - 1);
      if (c < COLS - 1) push(t + 1);
    }
    const id = touchesBorder ? OUTDOOR : next++;
    for (const t of members) out[t] = id;

    function push(n: number): void {
      if (out[n] !== -1) return;
      if (solidForRooms(g, n)) {
        out[n] = OUTDOOR;
        return;
      }
      out[n] = -2;
      stack[sp++] = n;
    }
  }
  // Any solid tile left unassigned is outdoors by definition.
  for (let t = 0; t < TILES; t++) if (out[t] < 0) out[t] = OUTDOOR;
  return next;
}

/** How many tiles are in this room. Used by the fuel curve, and re-derived by M6. */
export function roomSize(rooms: Int32Array, id: number): number {
  if (id === OUTDOOR) return 0;
  let n = 0;
  for (let t = 0; t < TILES; t++) if (rooms[t] === id) n++;
  return n;
}

// ── Dijkstra ────────────────────────────────────────────────────────────────

/**
 * A binary heap over (dist, tile). Written here rather than taken from a library because the
 * simulation may not allocate per tick and must break ties deterministically: equal distances are
 * ordered by TILE INDEX, so two devices — and the auditor — walk the board in the same order. An
 * unstable tie is not a cosmetic difference, it is a different colony.
 */
class Heap {
  private d = new Int32Array(TILES + 1);
  private t = new Int32Array(TILES + 1);
  private n = 0;

  clear(): void {
    this.n = 0;
  }

  push(dist: number, tile: number): void {
    let i = this.n++;
    this.d[i] = dist;
    this.t[i] = tile;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.less(i, p)) {
        this.swap(i, p);
        i = p;
      } else break;
    }
  }

  pop(): number {
    if (this.n === 0) return -1;
    const top = this.t[0];
    this.n--;
    if (this.n > 0) {
      this.d[0] = this.d[this.n];
      this.t[0] = this.t[this.n];
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (l < this.n && this.less(l, m)) m = l;
        if (r < this.n && this.less(r, m)) m = r;
        if (m === i) break;
        this.swap(i, m);
        i = m;
      }
    }
    return top;
  }

  get size(): number {
    return this.n;
  }

  private less(a: number, b: number): boolean {
    return this.d[a] !== this.d[b] ? this.d[a] < this.d[b] : this.t[a] < this.t[b];
  }

  private swap(a: number, b: number): void {
    const d = this.d[a];
    const t = this.t[a];
    this.d[a] = this.d[b];
    this.t[a] = this.t[b];
    this.d[b] = d;
    this.t[b] = t;
  }
}

const HEAP = new Heap();

/**
 * Cost from `source` to every tile, written into `out`. The cost of a path is the sum of the cost of
 * ENTERING each tile, so the source itself is 0 and stepping onto deep snow is expensive.
 */
export function costField(g: GraphInput, source: number, out: Int32Array): void {
  out.fill(INF);
  if (source < 0 || source >= TILES) return;
  out[source] = 0;
  HEAP.clear();
  HEAP.push(0, source);

  while (HEAP.size > 0) {
    const t = HEAP.pop();
    const d = out[t];
    const c = colOf(t);
    const r = rowOf(t);
    if (r > 0) relax(t - COLS, d);
    if (r < ROWS - 1) relax(t + COLS, d);
    if (c > 0) relax(t - 1, d);
    if (c < COLS - 1) relax(t + 1, d);
  }

  function relax(n: number, d: number): void {
    const step = stepCost(g, n);
    if (step >= INF) return;
    const nd = d + step;
    if (nd < out[n]) {
      out[n] = nd;
      HEAP.push(nd, n);
    }
  }
}

/**
 * The first step of the cheapest route from `from` to `to`, given a field grown from `to`.
 *
 * Walking DOWN a field grown from the destination is exact and needs no path reconstruction, and the
 * tie-break is the tile index so two devices take the same corridor. The asymmetry is deliberate and
 * correct: the field is grown from the destination, so `field[from]` is the cost of the journey and
 * the greedy descent is optimal.
 */
export function stepToward(g: GraphInput, field: Int32Array, from: number): number {
  let best = -1;
  let bestD = field[from];
  const c = colOf(from);
  const r = rowOf(from);
  consider(r > 0 ? from - COLS : -1);
  consider(r < ROWS - 1 ? from + COLS : -1);
  consider(c > 0 ? from - 1 : -1);
  consider(c < COLS - 1 ? from + 1 : -1);
  return best;

  function consider(n: number): void {
    if (n < 0) return;
    if (!passable(g, n)) return;
    const d = field[n];
    if (d >= INF) return;
    if (d < bestD || (d === bestD && best >= 0 && n < best)) {
      if (d < bestD) {
        bestD = d;
        best = n;
      } else if (n < best) best = n;
    }
  }
}
